// ============================================================================
// EnhancedConversationalChatbot.js (MAIN COMPONENT - SIMPLIFIED)
// ============================================================================
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, X, List, ArrowLeft } from 'lucide-react';
import { API_CONFIG } from '../../config/api';
import FormattedAIResponse from './utils/formatter';

// Import all components
import TypingIndicator from './TypingIndicator';
import NaturalFollowUp from './NaturalFollowUp';
import CompanySelectionButtons from './CompanySelectionButtons';
import ChartVisualization from './ChartVisualization';
import ResponsiveTable from './ResponsiveTable';
import CompanyDropdown from './CompanyDropdown';
import { ClarificationModal } from './ClarificationModal';
import InlineFeedback from '../feedback/InlineFeedback';
import HonestFailure from './HonestFailure';

// Import utilities
import { exportToExcel, exportToPDF } from './utils/exportHelpers';
import { saveQueryToHistory } from '../../utils/queryStorage';

const EnhancedConversationalChatbot = ({ onOpenQueries }) => {
  // State management
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(`session_${Date.now()}`);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [conversationState, setConversationState] = useState(null);
  const [companySelectionMode, setCompanySelectionMode] = useState(true);
  const [pendingCompanyMatches, setPendingCompanyMatches] = useState(null);
  const [isPaginating, setIsPaginating] = useState(false);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const [clarificationState, setClarificationState] = useState(null);
  const [pendingQuery, setPendingQuery] = useState(null);
  
  const messagesEndRef = useRef(null);
  const companiesFetchedRef = useRef(false);
  const abortControllerRef = useRef(null);

  // Initialize - fetch companies
  useEffect(() => {
    document.title = 'Pool Brain Invoice & Payment Manager AI Assistant';
    
    if (!companiesFetchedRef.current) {
      companiesFetchedRef.current = true;
      
      const loadingMessage = {
        type: 'assistant',
        content: 'Welcome! 👋 Setting things up for you...',
        timestamp: new Date().toISOString(),
        id: Date.now(),
        isLoading: true
      };
      setMessages([loadingMessage]);
      
      fetchCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show greeting after companies load
  useEffect(() => {
    if (!companiesLoading && companies.length > 0 && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.isLoading) {
        const greeting = {
          type: 'assistant',
          content: `Welcome to the Poolbrain System! 🎉\n\nTo get started, please tell me which company you'd like to work with. You can type the company name or say "all companies" to view data across all companies.`,
          timestamp: new Date().toISOString(),
          id: Date.now(),
          isCompanySelection: true
        };
        
        setMessages([greeting]);
      }
    }
  }, [companiesLoading, companies, messages]);

  // Fetch companies from API
  const fetchCompanies = useCallback(async () => {
    try {
      setCompaniesLoading(true);
      // No Content-Type header on a GET: it has no body, and a non-simple header
      // forces an unnecessary CORS preflight (OPTIONS). Plain GET = simple request.
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.COMPANIES}`, {
        method: 'GET'
      });

      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      } else {
        setCompanies([{ companyId: 'all', companyName: 'All Companies', location: 'All Locations' }]);
      }
    } catch (error) {
      setCompanies([{ companyId: 'all', companyName: 'All Companies', location: 'All Locations' }]);
    } finally {
      setCompaniesLoading(false);
    }
  }, []);

  // Match company from user input
  const matchCompanyFromInput = useCallback((input) => {
    const inputLower = input.toLowerCase().trim();
    
    if (inputLower.includes('all compan') || inputLower === 'all') {
      return [companies.find(c => c.companyId === 'all')];
    }
    
    const exactMatches = companies.filter(c => 
      c.companyName.toLowerCase() === inputLower && c.companyId !== 'all'
    );
    
    if (exactMatches.length > 0) return exactMatches;
    
    const partialMatches = companies.filter(c => 
      c.companyId !== 'all' && 
      (c.companyName.toLowerCase().includes(inputLower) || 
       inputLower.includes(c.companyName.toLowerCase()))
    );
    
    return partialMatches;
  }, [companies]);

  // Auto-scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (!isPaginating) {
      setTimeout(() => {
        if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [isPaginating]);

  useEffect(() => {
    if (messages.length > lastMessageCount && !isPaginating) {
      setLastMessageCount(messages.length);
      scrollToBottom();
    }
  }, [messages.length, lastMessageCount, scrollToBottom, isPaginating]);

  // Parse user intent
  const parseUserResponse = useCallback((userInput) => {
    const input = userInput.toLowerCase().trim();


    const intents = {
      showTable: ['show table', 'view table', 'see table', 'show data', 'tabular format', 'create table', 'tabular', 'tabular view', 'data table', 'data in table', 'detailed table'],
      showCharts: ['show chart', 'show charts', 'chart', 'charts', 'graph', 'graphs', 'visualize', 'plot', 'bar chart', 'pie chart', 'line chart', 'area chart', 'data visualization', 'show graph', 'show visual'],
      requestExport: ['export', 'download', 'save', 'export data', 'download data', 'save data', 'export records', 'download records', 'save records', 'export file', 'download file', 'save file', 'get file', 'get data', 'get records'],
      exportExcel: ['excel', 'spreadsheet', 'xls', 'xlsx', 'export to excel', 'download excel', 'save as excel', 'export spreadsheet', 'download spreadsheet', 'save as spreadsheet', 'export xls', 'download xls', 'save as xls', 'export xlsx', 'download xlsx', 'save as xlsx'],
      exportPDF: ['pdf', 'print', 'export to pdf', 'download pdf', 'save as pdf', 'export pdf', 'download pdf', 'save pdf', 'export to pdf file', 'download pdf file', 'save as pdf file', 'export pdf file', 'download pdf file', 'save pdf file']
    };

    // Match whole words/phrases only so inputs like "profitability" don't match "table"
    const matchesPattern = (p) => {
      const escaped = p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`\\b${escaped}\\b`).test(input);
    };

    if (intents.exportExcel.some(matchesPattern)) return 'exportExcel';
    if (intents.exportPDF.some(matchesPattern)) return 'exportPDF';

    for (const [intent, patterns] of Object.entries(intents)) {
      if (patterns.some(matchesPattern)) return intent;
    }

    return 'unknown';
  }, []);

  // Handle pagination
  const handlePagination = useCallback(async (page, messageId) => {
    if (isLoading || isPaginating) return;
    
    setIsPaginating(true);
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/conversation-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: 'show table',
          page: page,
          session_id: sessionId,
          company_id: selectedCompany || 'all',
          request_type: 'table'
        })
      });

      const data = await response.json();

      if (response.ok && data.type === 'show_table') {
        setMessages(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, data: data.data, timestamp: new Date().toISOString() }
            : msg
        ));
      }
    } catch (error) {
      console.error('Pagination error:', error);
    } finally {
      setIsLoading(false);
      setTimeout(() => setIsPaginating(false), 500);
    }
  }, [isLoading, isPaginating, sessionId, selectedCompany]);

  // Handle export with format
  const handleExportWithFormat = useCallback(async (format) => {
    if (!conversationState?.data) return;

    setIsLoading(true);
    
    try {
      const exportMessage = {
        type: 'assistant',
        content: `Preparing complete ${format.toUpperCase()} export...`,
        timestamp: new Date().toISOString(),
        id: Date.now()
      };
      setMessages(prev => [...prev, exportMessage]);

      const dataResponse = await fetch(`${API_CONFIG.BASE_URL}/api/conversation-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: 'fetch complete data',
          session_id: sessionId,
          company_id: selectedCompany || 'all',
          request_type: 'export'
        })
      });

      const completeData = await dataResponse.json();

      if (completeData.complete_data && completeData.complete_data.length > 0) {
        const exportData = {
          results: completeData.complete_data,
          total_count: completeData.complete_data.length
        };
        
        if (format === 'excel') {
          exportToExcel(exportData, `complete-export-${Date.now()}`);
        } else {
          exportToPDF(exportData, conversationState.query || 'Complete Data Export');
        }

        const successMessage = {
          type: 'assistant',
          content: `Complete ${format.toUpperCase()} export started! Your file contains all ${exportData.total_count} records.`,
          timestamp: new Date().toISOString(),
          id: Date.now() + 1
        };
        setMessages(prev => [...prev, successMessage]);

        setTimeout(() => {
                const followUpMessage = {
                  type: 'assistant',
                  content: '',
                  timestamp: new Date().toISOString(),
                  id: Date.now() + 2,
                  showFormattedFollowUp: true,
                  followUpData: {
                    message: `What would you like to do next?`,
                    hasData: true,
                    hasCharts: !!conversationState.data?.sql
                  }
                };
                setMessages(prev => [...prev, followUpMessage]);
              }, 500);
      }
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, [conversationState, selectedCompany, sessionId]);

  // Handle company selection (from buttons in chat)
  const handleCompanySelection = useCallback((company) => {
    setSelectedCompany(company.companyId);
    setCompanySelectionMode(false);
    setPendingCompanyMatches(null);

    const confirmMessage = {
      type: 'assistant',
      content: `Perfect! You've selected **${company.companyName}**. Now you can ask me about invoices, payments, customer billing, or any related data.`,
      timestamp: new Date().toISOString(),
      id: Date.now(),
      isHumanResponse: true
    };
    setMessages(prev => [...prev, confirmMessage]);
  }, []);

  // Handle company selection from dropdown
  const handleDropdownCompanySelect = useCallback((company) => {
    const previousCompany = selectedCompany;
    setSelectedCompany(company.companyId);
    setCompanySelectionMode(false);
    setPendingCompanyMatches(null);
    setConversationState(null);

    // Only add message if company actually changed
    if (previousCompany !== company.companyId) {
      const switchMessage = {
        type: 'assistant',
        content: previousCompany
          ? `Switched to **${company.companyName}**. Now you can ask me about routes, technicians, jobs, or any related data.\n\nTry asking questions like:\n• "Show routes with the technicians"\n• "List unassigned routes"\n• "Find routes spanning more than 5 different zip codes?"`
          : `You've selected **${company.companyName}**. Now you can ask me about routes, technicians, jobs, or any related data.\n\nTry asking questions like:\n• "Show routes with the technicians"\n• "List unassigned routes"\n• "Find routes spanning more than 5 different zip codes?"`,
        timestamp: new Date().toISOString(),
        id: Date.now(),
        isHumanResponse: true     
      };
      setMessages(prev => [...prev, switchMessage]);
    }
  }, [selectedCompany]);

  // Cancel ongoing query
  const handleCancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoading(false);

    const cancelMessage = {
      type: 'assistant',
      content: 'Query cancelled.',
      timestamp: new Date().toISOString(),
      id: Date.now()
    };
    setMessages(prev => [...prev, cancelMessage]);
  }, []);

  // Submit query - define BEFORE clarification handlers so they can reference it
  const handleSubmit = useCallback(async (query, page = 1) => {
    if (!query.trim() || isLoading) return;

    // Create new AbortController for this request.
    // Attach an explicit timeout (from API_CONFIG.TIMEOUT) so slow backend
    // responses don't get killed by the default browser/proxy timeout mid-flight.
    abortControllerRef.current = new AbortController();
    const requestTimeoutId = setTimeout(() => {
      if (abortControllerRef.current) {
        console.warn(`Request aborted after ${API_CONFIG.TIMEOUT}ms timeout`);
        abortControllerRef.current.abort();
      }
    }, API_CONFIG.TIMEOUT);

    const userMessage = {
      type: 'user',
      content: query,
      timestamp: new Date().toISOString(),
      id: Date.now()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const changeCompanyPhrases = [];
      if (!companySelectionMode && selectedCompany && changeCompanyPhrases.some(phrase => query.toLowerCase().includes(phrase))) {
        setSelectedCompany(null);
        setCompanySelectionMode(true);
        setPendingCompanyMatches(null);
        setConversationState(null);
        
        const changeMessage = {
          type: 'assistant',
          content: `No problem! Let's switch companies. Please tell me which company you'd like to work with.`,
          timestamp: new Date().toISOString(),
          id: Date.now() + 1,
          isCompanySelection: true
        };
        setMessages(prev => [...prev, changeMessage]);
        setIsLoading(false);
        return;
      }
      
      if (companySelectionMode && !selectedCompany) {
        const matches = matchCompanyFromInput(query);
        
        if (matches.length === 0) {
          const errorMessage = {
            type: 'assistant',
            content: `I couldn't find a company matching "${query}". Please try again with one of the available company names, or say "all companies" to view data across all companies.`,
            timestamp: new Date().toISOString(),
            id: Date.now() + 1,
            isCompanySelection: true
          };
          setMessages(prev => [...prev, errorMessage]);
          setIsLoading(false);
          return;
        }
        
        if (matches.length === 1) {
          const selectedCompanyObj = matches[0];
          setSelectedCompany(selectedCompanyObj.companyId);
          setCompanySelectionMode(false);
          
          const confirmMessage = {
            type: 'assistant',
            content: `Great! You've selected **${selectedCompanyObj.companyName}**. Now you can ask me about routes, technicians, jobs, or any related data for this company.\n\nTry asking questions like:\n• "Show routes with the technicians"\n• "List unassigned routes"\n• "Find routes spanning more than 5 different zip codes?"`,
            timestamp: new Date().toISOString(),
            id: Date.now() + 1,
            isHumanResponse: true
          };
          setMessages(prev => [...prev, confirmMessage]);
          setIsLoading(false);
          return;
        }
        
        if (matches.length > 1) {
          setPendingCompanyMatches(matches);
          
          const optionsMessage = {
            type: 'assistant',
            content: `I found ${matches.length} companies matching "${query}". Please select one:`,
            timestamp: new Date().toISOString(),
            id: Date.now() + 1,
            showCompanyOptions: true,
            companyOptions: matches
          };
          setMessages(prev => [...prev, optionsMessage]);
          setIsLoading(false);
          return;
        }
      }
      
      if (pendingCompanyMatches) {
        const selectedFromOptions = pendingCompanyMatches.find(c => 
          c.companyName.toLowerCase() === query.toLowerCase().trim() ||
          c.companyId === query
        );
        
        if (selectedFromOptions) {
          setSelectedCompany(selectedFromOptions.companyId);
          setCompanySelectionMode(false);
          setPendingCompanyMatches(null);
          
          const confirmMessage = {
            type: 'assistant',
            content: `Perfect! You've selected **${selectedFromOptions.companyName}**. Now you can ask me about invoices, payments, customer billing, or any related data.\n\nWhat would you like to know?`,
            timestamp: new Date().toISOString(),
            id: Date.now() + 1,
            isHumanResponse: true
          };
          setMessages(prev => [...prev, confirmMessage]);
          setIsLoading(false);
          return;
        }
      }

      if (conversationState && conversationState.hasData) {
        const intent = parseUserResponse(query);
        console.log('Parsed intent:', intent, 'from query:', query);
        
        if (intent === 'showTable') {
          console.log('Showing table - fetching from backend');
          
          const messageId = Date.now() + 1;
          const loadingMessage = {
            type: 'assistant',
            content: "Loading table data...",
            timestamp: new Date().toISOString(),
            id: messageId,
            isAnalyzing: true
          };
          setMessages(prev => [...prev, loadingMessage]);
          
          try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/conversation-chat`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                query: 'show table',
                page: page,
                session_id: sessionId,
                company_id: selectedCompany || 'all',
                request_type: 'table'
              })
            });

            const data = await response.json();
            
            if (response.ok && data.type === 'show_table') {
              const paginatedData = {
                results: data.data.results,
                page: data.data.page,
                page_size: data.data.page_size,
                total_count: data.data.total_count,
                has_more: data.data.has_more,
                sql: data.data.sql
              };

              setMessages(prev => prev.filter(msg => msg.id !== messageId));

              const assistantMessage = {
                type: 'assistant',
                content: "Here's the detailed table view of your data:",
                data: paginatedData,
                showTable: true,
                timestamp: new Date().toISOString(),
                id: Date.now() + 1,
                hasRecords: paginatedData.results.length > 0,
                queryCategory: conversationState.queryCategory,
                originalQuery: conversationState.query
              };
              
              setMessages(prev => [...prev, assistantMessage]);

              setTimeout(() => {
                const followUpMessage = {
                  type: 'assistant',
                  content: '',
                  timestamp: new Date().toISOString(),
                  id: Date.now() + 2,
                  showFormattedFollowUp: true,
                  followUpData: {
                    message: `Table displayed with ${data.data.total_count} total records! What would you like to do next?`,
                    hasData: true,
                    hasCharts: !!conversationState.data?.sql
                  }
                };
                setMessages(prev => [...prev, followUpMessage]);
              }, 500);
            }
          } catch (error) {
            console.error('Failed to load table:', error);
            setMessages(prev => prev.filter(msg => msg.id !== messageId));
            const errorMessage = {
              type: 'assistant',
              content: "Failed to load table. Please try again.",
              timestamp: new Date().toISOString(),
              id: Date.now() + 1
            };
            setMessages(prev => [...prev, errorMessage]);
          }
          setIsLoading(false);
          return;
        }
        
        if (intent === 'requestExport') {
          const messageId = Date.now() + 1;
          setMessages(prev => [...prev, {
            type: 'assistant',
            content: 'I can export your data to Excel or PDF. Just say "export to excel" or "export to pdf" and I\'ll prepare the complete dataset for you!',
            timestamp: new Date().toISOString(),
            id: messageId
          }]);
          setIsLoading(false);
          return;
        }
        
        if (intent === 'exportExcel' || intent === 'exportPDF') {
          const format = intent === 'exportExcel' ? 'excel' : 'pdf';
          await handleExportWithFormat(format);
          setIsLoading(false);
          return;
        }
        
        if (intent === 'showCharts') {
          const messageId = Date.now() + 1;
          let assistantMessage = {
            type: 'assistant',
            content: "Let me analyze your data for visual insights...",
            timestamp: new Date().toISOString(),
            id: messageId,
            isAnalyzing: true
          };
          setMessages(prev => [...prev, assistantMessage]);
          
          try {
            // First, fetch complete data for charts
            const dataResponse = await fetch(`${API_CONFIG.BASE_URL}/api/conversation-chat`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                query: 'fetch complete data',
                session_id: sessionId,
                company_id: selectedCompany || 'all',
                request_type: 'chart'
              })
            });

            const completeData = await dataResponse.json();
            
            if (!completeData.complete_data || completeData.complete_data.length === 0) {
              setMessages(prev => prev.map(msg =>
                msg.id === messageId
                  ? {
                      ...msg,
                      content: "No data available for charts.",
                      isAnalyzing: false
                    }
                  : msg
              ));
              setIsLoading(false);
              return;
            }

            if (completeData.complete_data.length < 2) {
              setMessages(prev => prev.map(msg =>
                msg.id === messageId
                  ? {
                      ...msg,
                      content: "Not enough data to generate a meaningful chart. Charts require at least 2 data points.",
                      isAnalyzing: false
                    }
                  : msg
              ));
              setIsLoading(false);
              return;
            }

            if (!conversationState.data?.sql) {
              setMessages(prev => prev.map(msg =>
                msg.id === messageId
                  ? {
                      ...msg,
                      content: "I couldn't generate charts for this data, but you can view the detailed table instead.",
                      isAnalyzing: false
                    }
                  : msg
              ));
              setIsLoading(false);
              return;
            }

            // Now analyze for charts
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CHARTS}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                sql_query: conversationState.data?.sql,
                query: conversationState.query,
                query_category: conversationState.queryCategory || 'invoice',
                company_id: selectedCompany || 'all',
                session_id: sessionId
              })
            });

            const chartData = await response.json();
            
            setMessages(prev => prev.map(msg => 
              msg.id === messageId 
                ? {
                    ...msg, 
                    content: chartData.recommendations && chartData.recommendations.length > 0 
                      ? "Here are some visual insights from your data:"
                      : "I couldn't generate charts for this data, but you can view the detailed table instead.",
                    isAnalyzing: false,
                    showCharts: chartData.recommendations && chartData.recommendations.length > 0,
                    chartRecommendations: chartData.recommendations || []
                  }
                : msg
            ));

            setTimeout(() => {
              const followUpMessage = {
                type: 'assistant',
                content: '',
                timestamp: new Date().toISOString(),
                id: Date.now() + 2,
                showFormattedFollowUp: true,
                followUpData: {
                  message: "Charts displayed! Want to export this data or view the detailed table?",
                  hasData: true,
                  hasCharts: false
                }
              };
              setMessages(prev => [...prev, followUpMessage]);
            }, 500);
            
          } catch (error) {
            console.error('Chart generation failed:', error);
            setMessages(prev => prev.map(msg => 
              msg.id === messageId 
                ? {
                    ...msg, 
                    content: "I couldn't generate charts for this data, but you can view the detailed table instead.",
                    isAnalyzing: false
                  }
                : msg
            ));
          }
          
          setIsLoading(false);
          return;
        }
      }

      console.log('Making new query request');
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/conversation-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          page: 1,
          session_id: sessionId,
          company_id: selectedCompany || 'all',
          request_type: 'query'
        }),
        signal: abortControllerRef.current?.signal
      });

      const data = await response.json();
      console.log('Received response:', data);

      // Log query to localStorage for developer feedback dashboard
      saveQueryToHistory({
        originalQuery: query,
        normalizedQuery: data.normalized_query || query,
        generatedSQL: data.data_summary?.sql || '',
        queryCategory: data.data_summary?.query_category || '',
        companyId: selectedCompany || 'all',
        sessionId: sessionId,
        hasError: data.type !== 'success',
        errorMessage: data.type !== 'success' ? (data.content || data.error || '') : ''
      });

      // Action link shortcut: user asked to add/create/open a page.
      // Render as a clickable button, skip SQL.
      if (response.ok && data.type === 'action_link') {
        setMessages(prev => [...prev, {
          type: 'assistant',
          isActionLink: true,
          actionUrl: data.url,
          actionName: data.display_name,
          actionInstructions: data.instructions,
          content: data.instructions || '',
          timestamp: new Date().toISOString(),
          id: Date.now() + 1,
        }]);
        setIsLoading(false);
        abortControllerRef.current = null;
        return;
      }

      // Report link shortcut: user asked for a named report.
      if (response.ok && data.type === 'report_link') {
        setMessages(prev => [...prev, {
          type: 'assistant',
          isReportLink: true,
          reportUrl: data.report_url,
          reportName: data.report_name,
          content: '',
          timestamp: new Date().toISOString(),
          id: Date.now() + 1,
        }]);
        setIsLoading(false);
        abortControllerRef.current = null;
        return;
      }

      // Backend may have auto-detected a display/export command via the LLM
      // (e.g. "report", "data", "pdf") and returned a table directly.
      // Render the table and short-circuit.
      if (response.ok && data.type === 'show_table') {
        const paginatedData = {
          results: data.data?.results || [],
          page: data.data?.page || 1,
          page_size: data.data?.page_size,
          total_count: data.data?.total_count,
          has_more: data.data?.has_more,
          sql: data.data?.sql
        };

        setMessages(prev => [...prev, {
          type: 'assistant',
          content: data.content || "Here's the detailed table view of your data:",
          data: paginatedData,
          showTable: true,
          timestamp: new Date().toISOString(),
          id: Date.now() + 1,
          hasRecords: paginatedData.results.length > 0,
          queryCategory: conversationState?.queryCategory,
          originalQuery: conversationState?.query || query
        }]);
        setIsLoading(false);
        abortControllerRef.current = null;
        return;
      }

      // Backend may have auto-detected an export command (pdf/excel/download)
      // and returned the complete dataset for export. Trigger the client-side
      // export flow so the user gets the file immediately.
      if (response.ok && (data.type === 'download_export' || data.type === 'show_export')) {
        const exportData = {
          results: data.data?.results || data.complete_data || [],
          total_count: data.data?.total_count || (data.complete_data ? data.complete_data.length : 0)
        };
        if (exportData.results.length > 0) {
          // Default to Excel unless the raw query mentioned PDF
          const wantsPdf = /\bpdf\b/i.test(query);
          if (wantsPdf) {
            exportToPDF(exportData, conversationState?.query || query);
          } else {
            exportToExcel(exportData, `export-${Date.now()}`);
          }
          setMessages(prev => [...prev, {
            type: 'assistant',
            content: `Export started — your file contains ${exportData.total_count} record(s).`,
            timestamp: new Date().toISOString(),
            id: Date.now() + 1
          }]);
        } else {
          setMessages(prev => [...prev, {
            type: 'assistant',
            content: "I couldn't find any data to export.",
            timestamp: new Date().toISOString(),
            id: Date.now() + 1
          }]);
        }
        setIsLoading(false);
        abortControllerRef.current = null;
        return;
      }

      if (response.ok && data.type === 'success') {
        // Check if clarification is needed (FLAW-16 integration)
        if (data.needs_clarification) {
          setPendingQuery(query);
          setClarificationState({
            needs_clarification: true,
            message: data.clarification_message || data.message || 'Please clarify your request',
            ambiguity_score: data.ambiguity_score || 0.5,
            options: data.clarification_options || data.options || []
          });

          // Add message indicating clarification is needed
          const clarificationNeededMessage = {
            type: 'assistant',
            content: data.clarification_message || 'I need clarification to answer your question correctly.',
            timestamp: new Date().toISOString(),
            id: Date.now() + 1,
            isClarificationNeeded: true
          };
          setMessages(prev => [...prev, clarificationNeededMessage]);
          setIsLoading(false);
          return;
        }

        const selectedCompanyName = companies.find(c => c.companyId === selectedCompany)?.companyName || 'All Companies';

        const totalCount = data.data_summary?.total_count || 0;
        console.log('Total count:', totalCount);

        setConversationState({
          hasData: totalCount > 0,
          data: {
            sql: data.data_summary?.sql,
            total_count: totalCount,
            aggregates: data.data_summary?.aggregates
          },
          query: query,
          queryCategory: data.data_summary?.query_category,
          formatRecommendation: data.format_recommendation || null,
          messageId: Date.now() + 1
        });

        const assistantMessage = {
          type: 'assistant',
          content: data.content || `${totalCount} ${totalCount === 1 ? 'record' : 'records'} found for ${selectedCompanyName}`,
          timestamp: new Date().toISOString(),
          id: Date.now() + 1,
          isHumanResponse: true,
          queryCategory: data.data_summary?.query_category,
          companyId: selectedCompany,
          originalQuery: query,
          hasRecords: totalCount > 0,
          generatedSql: data.data_summary?.sql || '',
          usedFeedbackCache: data.used_feedback_cache || false,
        };

        setMessages(prev => [...prev, assistantMessage]);

        if (totalCount > 0) {
          const recFormat = data.format_recommendation?.primary_format;
          const autoDisplay = data.format_recommendation?.auto_display === true;
          const hasCharts = data.data_summary?.has_charts ?? false;
          const sqlQuery = data.data_summary?.sql;
          const qCategory = data.data_summary?.query_category || 'general';

          // ── AUTO-DISPLAY TABLE only when backend confirms explicit display intent ──
          if (autoDisplay && (recFormat === 'TABLE' || recFormat === 'PIVOT_TABLE')) {
            try {
              const tableResponse = await fetch(`${API_CONFIG.BASE_URL}/api/conversation-chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  query: 'show table',
                  page: 1,
                  session_id: sessionId,
                  company_id: selectedCompany || 'all',
                  request_type: 'table'
                })
              });
              const tableData = await tableResponse.json();

              if (tableResponse.ok && tableData.type === 'show_table') {
                const paginatedData = {
                  results: tableData.data.results,
                  page: tableData.data.page,
                  page_size: tableData.data.page_size,
                  total_count: tableData.data.total_count,
                  has_more: tableData.data.has_more,
                  sql: tableData.data.sql
                };
                const tableMessage = {
                  type: 'assistant',
                  content: '',
                  data: paginatedData,
                  showTable: true,
                  timestamp: new Date().toISOString(),
                  id: Date.now() + 2,
                  hasRecords: paginatedData.results.length > 0,
                  queryCategory: qCategory,
                  originalQuery: query
                };
                setMessages(prev => [...prev, tableMessage]);

                // Follow-up: offer charts/export (table already shown)
                setTimeout(() => {
                  setMessages(prev => [...prev, {
                    type: 'assistant',
                    content: '',
                    timestamp: new Date().toISOString(),
                    id: Date.now() + 3,
                    showFormattedFollowUp: true,
                    followUpData: {
                      message: `Showing ${tableData.data.total_count} records. You can also create charts, export data, or ask follow-up questions!`,
                      hasData: true,
                      hasCharts: hasCharts,
                      tableAlreadyShown: true
                    }
                  }]);
                }, 300);
              }
            } catch (err) {
              console.error('Auto-display table failed:', err);
              // Fallback: show normal follow-up
              setMessages(prev => [...prev, {
                type: 'assistant', content: '', timestamp: new Date().toISOString(),
                id: Date.now() + 2, showFormattedFollowUp: true,
                followUpData: { message: `I found ${totalCount} records! What would you like to do next?`, hasData: true, hasCharts }
              }]);
            }

          // ── AUTO-DISPLAY CHARTS only when backend confirms auto-display ──
          } else if (autoDisplay && ['BAR_CHART', 'LINE_CHART', 'AREA_CHART', 'PIE_CHART', 'SCATTER_PLOT'].includes(recFormat) && hasCharts && sqlQuery) {
            try {
              // Step 1: fetch complete data
              const dataResp = await fetch(`${API_CONFIG.BASE_URL}/api/conversation-chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ query: 'fetch complete data', session_id: sessionId, company_id: selectedCompany || 'all', request_type: 'chart' })
              });
              const completeData = await dataResp.json();

              if (completeData.complete_data && completeData.complete_data.length >= 2) {
                // Step 2: analyse for charts
                const chartResp = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CHARTS}`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ sql_query: sqlQuery, query, query_category: qCategory, company_id: selectedCompany || 'all', session_id: sessionId })
                });
                const chartData = await chartResp.json();

                if (chartData.recommendations && chartData.recommendations.length > 0) {
                  setMessages(prev => [...prev, {
                    type: 'assistant',
                    content: 'Here are some visual insights from your data:',
                    showCharts: true,
                    chartRecommendations: chartData.recommendations,
                    timestamp: new Date().toISOString(),
                    id: Date.now() + 2,
                    originalQuery: query
                  }]);

                  setTimeout(() => {
                    setMessages(prev => [...prev, {
                      type: 'assistant', content: '', timestamp: new Date().toISOString(),
                      id: Date.now() + 3, showFormattedFollowUp: true,
                      followUpData: { message: 'Charts displayed! Want to view the table or export the data?', hasData: true, hasCharts: false }
                    }]);
                  }, 300);
                } else {
                  // Charts couldn't be generated — show normal follow-up
                  setMessages(prev => [...prev, {
                    type: 'assistant', content: '', timestamp: new Date().toISOString(),
                    id: Date.now() + 2, showFormattedFollowUp: true,
                    followUpData: { message: `I found ${totalCount} records! What would you like to do next?`, hasData: true, hasCharts: false }
                  }]);
                }
              }
            } catch (err) {
              console.error('Auto-display charts failed:', err);
              setMessages(prev => [...prev, {
                type: 'assistant', content: '', timestamp: new Date().toISOString(),
                id: Date.now() + 2, showFormattedFollowUp: true,
                followUpData: { message: `I found ${totalCount} records! What would you like to do next?`, hasData: true, hasCharts }
              }]);
            }

          // ── DEFAULT: show follow-up prompt ──
          } else {
            setTimeout(() => {
              setMessages(prev => [...prev, {
                type: 'assistant',
                content: '',
                timestamp: new Date().toISOString(),
                id: Date.now() + 2,
                showFormattedFollowUp: true,
                followUpData: {
                  message: `I found ${totalCount} records! What would you like to do next?`,
                  hasData: true,
                  hasCharts: hasCharts
                }
              }]);
            }, 1000);
          }
        }
      } else {
        // B1 (Tier 1): honest_failure carries a structured explanation that
        // gets rendered as "I couldn't validate this — here's what I tried".
        const isHonestFailure = data.type === 'honest_failure' && data.failure_explanation;
        const errorMessage = {
          type: 'assistant',
          content: data.content || data.error || "I couldn't process that request. Could you try rephrasing?",
          timestamp: new Date().toISOString(),
          id: Date.now() + 1,
          isHumanResponse: true,
          failureExplanation: isHonestFailure ? data.failure_explanation : null,
          originalQuery: query,
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      // Don't show error if request was cancelled
      if (error.name === 'AbortError') {
        console.log('Request cancelled');
        return;
      }
      console.error('Request failed:', error);
      const errorMessage = {
        type: 'assistant',
        content: "I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date().toISOString(),
        id: Date.now() + 1
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      clearTimeout(requestTimeoutId);
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [isLoading, sessionId, selectedCompany, companies, conversationState, parseUserResponse, handleExportWithFormat, companySelectionMode, matchCompanyFromInput, pendingCompanyMatches]);

  // Handle clarification option selection (FLAW-16) - now after handleSubmit
  const handleClarificationSelect = useCallback((selectedOption) => {
    if (!pendingQuery) return;

    // Add user confirmation message
    const confirmationMessage = {
      type: 'user',
      content: `I meant: ${selectedOption}`,
      timestamp: new Date().toISOString(),
      id: Date.now()
    };
    setMessages(prev => [...prev, confirmationMessage]);

    // Close modal
    setClarificationState(null);

    // Re-submit query with clarification context
    const clarifiedQuery = `${pendingQuery} (specifically: ${selectedOption})`;
    setPendingQuery(null);
    
    // Retry with clarified query
    setInputValue('');
    handleSubmit(clarifiedQuery);
  }, [pendingQuery, handleSubmit]);

  // Handle clarification cancellation
  const handleClarificationCancel = useCallback(() => {
    setClarificationState(null);
    setPendingQuery(null);
  }, []);


  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: '#f1f5f9' }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #4F7CFF 0%, #3b5998 100%)', color: 'white', padding: '1rem 2rem' }}>
          <div style={{ maxWidth: '90rem', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ width: '60px', height: '60px', backgroundColor: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src="fav-icon.png" alt="Pool Brain" />
              </div>

              <div style={{ flex: 1 }}>
                <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '700' }}>
                  Pool Brain AI Assistant
                </h1>
                <p style={{ margin: 0, opacity: 0.9, fontSize: '0.9rem' }}>
                  Your intelligent assistant for managing Routes.
                </p>
              </div>

              <a
                href="https://pbdashboard.gate6.ai/"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.625rem 1rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '10px',
                  color: 'white',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                }}
              >
                <ArrowLeft style={{ width: '1rem', height: '1rem' }} />
                Back to Dashboard
              </a>

              <CompanyDropdown
                companies={companies}
                selectedCompany={selectedCompany}
                onSelectCompany={handleDropdownCompanySelect}
                isLoading={companiesLoading}
                disabled={isLoading}
              />

              {onOpenQueries && (
                <button
                  onClick={onOpenQueries}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '8px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <List style={{ width: '1rem', height: '1rem' }} />
                  Query History
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '2rem' }}>
          <div style={{ maxWidth: '80rem', margin: '0 auto', width: '100%', flex: 1 }}>
            {messages.map((msg) => (
              <div key={msg.id} style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '80rem',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  backgroundColor: msg.type === 'user' ? '#4F7CFF' : '#fff',
                  color: msg.type === 'user' ? 'white' : '#1e293b',
                  border: msg.type === 'assistant' ? '1px solid #e2e8f0' : 'none',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}>
                  {msg.type === 'user' ? msg.content : (
                    <>
                      {msg.isLoading ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <img src="pb.gif" alt="Pool Brain" />
                          <span>{msg.content}</span>
                        </div>
                      ) : msg.isHumanResponse ? (
                        <FormattedAIResponse content={msg.content} />
                      ) : msg.isReportLink ? (
                        <span>
                          Here is the{' '}
                          <a
                            href={msg.reportUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#4F7CFF', textDecoration: 'underline', fontWeight: 'bold' }}
                          >
                            {msg.reportName}
                          </a>
                        </span>
                      ) : msg.isActionLink ? (
                        <div>
                          <div style={{ fontWeight: 'bold', marginBottom: '0.75rem', color: '#1e293b' }}>
                            {msg.actionInstructions}
                          </div>
                          <a
                            href={msg.actionUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-block',
                              padding: '0.6rem 1.25rem',
                              backgroundColor: '#4F7CFF',
                              color: 'white',
                              textDecoration: 'none',
                              fontWeight: 'bold',
                              borderRadius: '8px',
                              boxShadow: '0 1px 3px rgba(79, 124, 255, 0.3)'
                            }}
                          >
                            Open {msg.actionName} →
                          </a>
                        </div>
                      ) : (
                        msg.content
                      )}
                      
                      {msg.showCompanyOptions && msg.companyOptions && (
                        <CompanySelectionButtons 
                          companies={msg.companyOptions}
                          onSelect={handleCompanySelection}
                        />
                      )}
                      
                      {msg.showFormattedFollowUp && (
                        <NaturalFollowUp
                          message={msg.followUpData?.message}
                          hasData={msg.followUpData?.hasData}
                          hasCharts={msg.followUpData?.hasCharts}
                          tableAlreadyShown={msg.followUpData?.tableAlreadyShown}
                        />
                      )}
                      
                      {msg.showTable && msg.data && (
                          <ResponsiveTable 
                            data={msg.data} 
                            onPageChange={handlePagination} 
                            isLoading={isLoading} 
                            originalQuery={msg.originalQuery}
                            messageId={msg.id}
                          />
                      )}
                      
                      {msg.showCharts && msg.chartRecommendations && (
                          <ChartVisualization
                            chartRecommendations={msg.chartRecommendations}
                            originalQuery={msg.originalQuery}
                          />
                      )}

                      {/* B1: structured failure explanation when the pipeline couldn't validate */}
                      {msg.failureExplanation && (
                        <HonestFailure failureExplanation={msg.failureExplanation} />
                      )}

                      {/* InlineFeedback disabled — feedback managed via Admin Panel > Query History */}
                      {false && msg.isHumanResponse && msg.originalQuery && (
                        <InlineFeedback
                          sessionId={sessionId}
                          originalQuery={msg.originalQuery}
                          generatedSql={msg.generatedSql || msg.data?.sql || ''}
                          companyId={msg.companyId || selectedCompany || 'all'}
                          usedFeedbackCache={msg.usedFeedbackCache || false}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}

            {isLoading && !isPaginating && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input Area */}
        <div style={{ backgroundColor: '#fff', borderTop: '1px solid #e2e8f0', padding: '1.5rem 2rem' }}>
          <div style={{ maxWidth: '80rem', margin: '0 auto', display: 'flex', gap: '1rem' }}>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(inputValue);
                }
              }}
              placeholder={
                companiesLoading
                  ? "Please wait..."
                  : companySelectionMode 
                    ? "Type a company name or 'all companies'..." 
                    : "Ask about routes , technicians, jobs, and more..."
              }
              style={{
                flex: 1,
                padding: '0.875rem 1.25rem',
                border: '1px solid #e2e8f0',
                borderRadius: '25px',
                fontSize: '1rem',
                outline: 'none'
              }}
              disabled={isLoading || companiesLoading}
            />
            {isLoading ? (
              <button
                onClick={handleCancel}
                style={{
                  padding: '0.875rem 1.5rem',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '25px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: '600',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
              >
                <X style={{ width: '1.25rem', height: '1.25rem' }} />
                Cancel
              </button>
            ) : (
              <button
                onClick={() => handleSubmit(inputValue)}
                disabled={!inputValue.trim() || companiesLoading}
                style={{
                  padding: '0.875rem 1.5rem',
                  backgroundColor: '#4F7CFF',
                  color: 'white',
                  border: 'none',
                  borderRadius: '25px',
                  cursor: !inputValue.trim() || companiesLoading ? 'not-allowed' : 'pointer',
                  opacity: !inputValue.trim() || companiesLoading ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: '600'
                }}
              >
                <Send style={{ width: '1.25rem', height: '1.25rem' }} />
                Ask
              </button>
            )}
          </div>
        </div>

        {/* FLAW-16 Clarification Modal */}
        <ClarificationModal
          isOpen={clarificationState?.needs_clarification || false}
          message={clarificationState?.message || ''}
          ambituityScore={clarificationState?.ambiguity_score || 0}
          options={clarificationState?.options || []}
          onSelect={handleClarificationSelect}
          onCancel={handleClarificationCancel}
          isLoading={isLoading}
        />
      </div>
  );
};

export default EnhancedConversationalChatbot;